// src/components/businesses/NotificacionesPanel.tsx
// Empresa → Notificaciones: a qué correos avisa el sistema y de qué.
//
//   · Correos (uno o varios, máx. 10): reciben cada conversación nueva, cada
//     prospecto nuevo y las alertas de clientes sin atender (supervisor).
//   · on_new_conversation — aviso por CORREO de cada conversación nueva.
//   · on_new_lead — aviso de cada prospecto que registra el agente.
//
// Orión GET/PUT /org/notificaciones (services/admin_notify.py). La org sale del
// JWT; guardar solo lo puede un admin (el servidor lo valida, no solo la UI).

import { useEffect, useState, type ReactNode } from 'react';
import { Bell, Loader2, Mail, MessageSquarePlus, Save, UserPlus, X } from 'lucide-react';

import { Card, ErrorNote, Loading } from '@/modules/orion-shell/ui';
import { OsApp } from '@/modules/orion-shell/OsApp';

import { getOrgNotificaciones, saveOrgNotificaciones, type OrgNotificaciones } from '@/lib/api';

type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string }) => void;

const MAX_CORREOS = 10;
const CORREO = /^[^@\s,;]+@[^@\s,;]+\.[^@\s,;]+$/;

export function NotificacionesPanel({ addNotification }: { addNotification?: Notifier }) {
  const [cfg, setCfg] = useState<OrgNotificaciones | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [nuevo, setNuevo] = useState('');
  const [errorCorreo, setErrorCorreo] = useState<string | null>(null);
  const [sucio, setSucio] = useState(false);

  useEffect(() => {
    getOrgNotificaciones()
      .then(setCfg)
      .catch((e) => addNotification?.({ type: 'error', title: 'No se pudo cargar', message: e?.message }))
      .finally(() => setLoading(false));
  }, []);

  const cambiar = (parche: Partial<OrgNotificaciones>) => {
    setCfg((c) => (c ? { ...c, ...parche } : c));
    setSucio(true);
  };

  /** Suma lo escrito en el campo a la lista. `null` si hay un correo inválido o demasiados. */
  const conPendientes = (): string[] | null => {
    if (!cfg) return null;
    const piezas = nuevo.split(/[\s,;]+/).map((x) => x.trim().toLowerCase()).filter(Boolean);
    const malos = piezas.filter((x) => !CORREO.test(x));
    if (malos.length) {
      setErrorCorreo(`No parece un correo: ${malos.join(', ')}`);
      return null;
    }
    const todos = Array.from(new Set([...cfg.emails, ...piezas]));
    if (todos.length > MAX_CORREOS) {
      setErrorCorreo(`Máximo ${MAX_CORREOS} correos.`);
      return null;
    }
    setErrorCorreo(null);
    setNuevo('');
    return todos;
  };

  const agregar = () => {
    if (!nuevo.trim()) return;
    const todos = conPendientes();
    if (todos) cambiar({ emails: todos });
  };

  const quitar = (e: string) => cfg && cambiar({ emails: cfg.emails.filter((x) => x !== e) });

  const guardar = async () => {
    if (!cfg) return;
    const emails = conPendientes();   // lo escrito sin «Agregar» también se guarda
    if (!emails) return;
    setBusy(true);
    try {
      const out = await saveOrgNotificaciones({
        emails, on_new_conversation: cfg.on_new_conversation, on_new_lead: cfg.on_new_lead,
      });
      setCfg(out);
      setSucio(false);
      addNotification?.({ type: 'success', title: 'Notificaciones guardadas' });
    } catch (e: any) {
      addNotification?.({ type: 'error', title: 'No se pudo guardar', message: e?.message });
    } finally {
      setBusy(false);
    }
  };

  const sinCorreos = !!cfg && cfg.emails.length === 0;
  const pendiente = sucio || !!nuevo.trim();
  const subtitulo = !cfg ? 'A quién avisamos y de qué'
    : sinCorreos ? 'Sin correos: no se avisa a nadie por correo'
    : `${cfg.emails.length} ${cfg.emails.length === 1 ? 'correo' : 'correos'} reciben los avisos`;

  return (
    <div className="w-full">
      <OsApp
        icon={Bell}
        title="Notificaciones"
        subtitle={subtitulo}
        actions={cfg && (
          <>
            {pendiente && <span className="hidden text-xs text-muted-foreground sm:inline">Cambios sin guardar</span>}
            <button type="button" onClick={guardar} disabled={busy || !pendiente}
              className="os-btn-brand inline-flex h-9 items-center gap-1.5 rounded-xl px-3 text-[13px] font-semibold">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Save className="h-4 w-4" aria-hidden />}
              Guardar
            </button>
          </>
        )}
      >
        {loading && <Loading />}
        {!loading && !cfg && <ErrorNote>No se pudo cargar la configuración de notificaciones.</ErrorNote>}
        {cfg && (
          <div className="grid grid-cols-1 items-start gap-3 sm:gap-4 lg:grid-cols-2">
            <Card title="Correos de notificación" titleId="notif-correos">
              <p className="mb-3 text-sm text-muted-foreground">
                Reciben cada conversación nueva, cada prospecto nuevo y las alertas de clientes que esperan a una persona del equipo.
              </p>
              {cfg.emails.length > 0 && (
                <ul className="mb-3 flex flex-wrap gap-2">
                  {cfg.emails.map((e) => (
                    <li key={e} className="flex max-w-full items-center gap-1.5 rounded-full border border-border bg-muted py-1 pl-2.5 pr-1 text-sm">
                      <Mail size={13} className="shrink-0 text-muted-foreground" aria-hidden />
                      <span className="min-w-0 break-all">{e}</span>
                      <button type="button" onClick={() => quitar(e)} aria-label={`Quitar ${e}`}
                        className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-muted-foreground hover:bg-background hover:text-foreground">
                        <X size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <label htmlFor="correo-nuevo" className="sr-only">Agregar correo</label>
              <div className="flex gap-2">
                <input
                  id="correo-nuevo" type="email" inputMode="email" value={nuevo}
                  onChange={(e) => { setNuevo(e.target.value); setErrorCorreo(null); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); agregar(); } }}
                  placeholder="correo@empresa.com"
                  className="h-10 min-w-0 flex-1 rounded-xl border border-input bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground"
                />
                <button type="button" onClick={agregar} disabled={!nuevo.trim()}
                  className="inline-flex h-10 items-center rounded-xl border border-border bg-card px-3 text-[13px] font-medium text-foreground hover:bg-muted disabled:opacity-50">
                  Agregar
                </button>
              </div>
              {errorCorreo && <p className="mt-2 text-xs text-destructive">{errorCorreo}</p>}
              {sinCorreos && <p className="mt-2 text-xs text-muted-foreground">Agrega al menos uno para que lleguen los avisos.</p>}
            </Card>

            <Card title="Qué se avisa" titleId="notif-que">
              <div className="flex flex-col divide-y divide-border">
                <Interruptor
                  icono={<MessageSquarePlus size={18} />} tono="violet"
                  titulo="Cada conversación nueva"
                  detalle="Un correo cada vez que alguien escribe por primera vez (WhatsApp, Messenger, Instagram…). Nunca por conversaciones del equipo ni de prueba."
                  activo={cfg.on_new_conversation}
                  onChange={(v) => cambiar({ on_new_conversation: v })}
                />
                <Interruptor
                  icono={<UserPlus size={18} />} tono="indigo"
                  titulo="Cada prospecto nuevo"
                  detalle={`Cuando el agente registra a alguien con interés real.${cfg.tiene_whatsapp_admin ? ' También llega por WhatsApp al número del administrador.' : ''}`}
                  activo={cfg.on_new_lead}
                  onChange={(v) => cambiar({ on_new_lead: v })}
                />
              </div>
            </Card>
          </div>
        )}
      </OsApp>
    </div>
  );
}

function Interruptor({ icono, tono, titulo, detalle, activo, onChange }: {
  icono: ReactNode; tono: string; titulo: string; detalle: string; activo: boolean; onChange: (v: boolean) => void;
}) {
  const id = `sw-${titulo.replace(/\W+/g, '-').toLowerCase()}`;
  return (
    <div className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
      <span className={`orion-tone tone-${tono} grid h-9 w-9 shrink-0 place-items-center rounded-xl`} aria-hidden>{icono}</span>
      <div className="min-w-0 flex-1">
        <p id={id} className="text-sm font-semibold text-foreground">{titulo}</p>
        <p className="text-xs leading-relaxed text-muted-foreground">{detalle}</p>
      </div>
      <button type="button" role="switch" aria-checked={activo} aria-labelledby={id} onClick={() => onChange(!activo)}
        className="os-switch relative mt-1 h-6 w-11 shrink-0 rounded-full">
        <span aria-hidden className="absolute left-[3px] top-[3px] h-[18px] w-[18px] rounded-full shadow" style={{ background: 'hsl(var(--primary-foreground))' }} />
      </button>
    </div>
  );
}
